/**
 * The estimate page's lifecycle rail (docs/features/journey-visual-language/
 * 01_proposal.md §2–§4), one mapper per kind of estimate:
 *
 * - a Tharstern or CIS-estimating estimate: Estimate ready · Customer
 *   response · Job raised, from its commercial status and its linked job;
 * - a merchandise draft: Requested · Ticket raised · Quote attached · Priced,
 *   from the dates the draft holds.
 *
 * The rail ends at what the estimate became. On main that is the Tharstern
 * job, so the last step is "Job raised" and links the job once one exists.
 *
 * The mappers only put into words what the response says. A date appears only
 * where the backend gave one. (A catalogue estimate's rail retired with the
 * estimate link, order to invoice step 3b.)
 */

import type { components } from "../data/api-schema.generated";
import type { MerchandiseDraftEstimate } from "../data/merchandise-types";
import { formatDateOrDash } from "./formatters";
import type { LifecycleRail, RailStep } from "./lifecycle-rail";

type EstimateStatus = components["schemas"]["EstimateDetailViewItem"]["status"];

/** The job an estimate became, as the detail response's `linked_job` names it. */
export interface RailJob {
  jobNo: string;
  /** The booking date, when the job record holds one. */
  orderedAt: string | null;
}

const date = formatDateOrDash;

function lines(...parts: Array<string | null | undefined | false>): string[] {
  return parts.filter((p): p is string => !!p).slice(0, 2);
}

function step(
  id: string,
  name: string,
  state: RailStep["state"],
  status: string[] = [],
  extra: Partial<Pick<RailStep, "problem" | "alert" | "link" | "info">> = {},
): RailStep {
  return {
    id,
    name,
    state,
    problem: extra.problem ?? null,
    status,
    alert: extra.alert ?? null,
    link: extra.link ?? null,
    detail: [],
    info: extra.info ?? null,
  };
}

/**
 * The ⓘ on the rail's last step. On main the estimate ends at the job,
 * because there are no CIS orders yet; when there are, it ends at Accepted
 * and the order carries artwork and payment (proposal §2).
 */
export const JOB_RAISED_INFO =
  "Once orders exist in One, the estimate ends at Accepted and the order takes over artwork and payment.";

/** The last step, with its ⓘ: whatever it says, it says why it is the last. */
function withJobInfo(job: RailStep): RailStep {
  return { ...job, info: JOB_RAISED_INFO };
}

function estimateReadyStep(on: string | null | undefined): RailStep {
  return step("ready", "Estimate ready", "done", lines(on && date(on)));
}

/** The last step when a job exists: its number (the link) and booking date. */
function jobDoneStep(job: RailJob, canLinkJob: boolean): RailStep {
  return step(
    "job",
    "Job raised",
    "done",
    lines(`Job ${job.jobNo}`, job.orderedAt && `Booked ${date(job.orderedAt)}`),
    { link: canLinkJob ? { kind: "job", jobNo: job.jobNo } : null },
  );
}

// ── A Tharstern or CIS-estimating estimate ────────────────────────────────

export interface TharsternEstimateRailInput {
  status: EstimateStatus;
  /** The estimate's own date (`estimate_date`). */
  estimateDate: string | null;
  job: RailJob | null;
  canLinkJob: boolean;
}

const RESPONSE_BY_STATUS: Record<
  EstimateStatus,
  { state: RailStep["state"]; status: string }
> = {
  open: { state: "in_progress", status: "Awaiting an outcome" },
  tender: { state: "in_progress", status: "Tender" },
  won: { state: "done", status: "Won" },
  lost: { state: "done", status: "Lost" },
  superseded: { state: "to_do", status: "Superseded" },
  stale: { state: "to_do", status: "Stale" },
};

/** A Tharstern-native or CIS-estimating estimate's rail, from its status and its job. */
export function railFromTharsternEstimate(
  input: TharsternEstimateRailInput,
): LifecycleRail {
  const response = RESPONSE_BY_STATUS[input.status];
  const job = input.job
    ? jobDoneStep(input.job, input.canLinkJob)
    : input.status === "won"
      ? step("job", "Job raised", "in_progress", ["No job linked yet"])
      : step("job", "Job raised", "to_do");
  return {
    steps: [
      estimateReadyStep(input.estimateDate),
      step("response", "Customer response", response.state, [response.status]),
      withJobInfo(job),
    ],
  };
}

// ── A merchandise draft estimate ──────────────────────────────────────────

/**
 * Requested · Ticket raised · Quote attached · Priced. A step is done when the
 * draft holds its date; the first that is not is in progress (none on a
 * withdrawn draft — nothing is coming next), and each undone step says what
 * it is waiting for. There is no "Sent" step: slice A's terminal state is
 * Priced, and a step that can never complete would read as work outstanding.
 */
export function railFromDraftEstimate(
  draft: MerchandiseDraftEstimate,
): LifecycleRail {
  const steps: Array<[string, string, string, string | null]> = [
    ["requested", "Requested", "Not yet requested", draft.createdAt],
    [
      "ticket_raised",
      "Ticket raised",
      "Ticket not yet raised",
      draft.merchandiseTeamTicket?.raisedAt ?? null,
    ],
    [
      "quote_attached",
      "Quote attached",
      "Awaiting quote",
      draft.linkedQuote?.linkedAt ?? null,
    ],
    ["priced", "Priced", "Awaiting pricing", draft.pricing?.pricedAt ?? null],
  ];
  let currentAssigned = false;
  return {
    steps: steps.map(([id, name, pending, on]) => {
      if (on) {
        const held = id === "priced" && draft.state === "cost_changed";
        return step(
          id,
          name,
          "done",
          [date(on)],
          held ? { problem: "at_risk", alert: "Cost changed since" } : {},
        );
      }
      if (!currentAssigned && draft.state !== "withdrawn") {
        currentAssigned = true;
        return step(id, name, "in_progress", [pending]);
      }
      return step(id, name, "to_do", [pending]);
    }),
  };
}
